import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Rocket, Sparkle, Buildings, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = { public: Rocket, premium: Sparkle, enterprise: Buildings };

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data.plans)); }, []);

  const choose = async (plan) => {
    if (plan.tier === "public") { nav("/explore"); return; }
    if (!user) { nav("/auth"); return; }
    const lookup_key = yearly ? plan.lookup_yearly : plan.lookup_monthly;
    setBusy(plan.tier);
    try {
      const res = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  const priceOf = (plan) => {
    if (plan.tier === "public") return "Free";
    return yearly ? `$${plan.yearly}` : `$${plan.monthly}`;
  };

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-28 pb-24">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Star size={18} weight="duotone" color="#00D8FF" />
            <span className="text-tel text-[10px] text-cyan-400/80">Access Tiers</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Choose your orbit.</h1>
          <p className="text-white/55 mt-5 max-w-lg mx-auto">Start free. Upgrade for unlimited Astrophysical Analysis, voice-narrated journeys, and enterprise-grade tools.</p>

          <div className="inline-flex items-center gap-1 mt-8 p-1 rounded-full bg-white/5">
            <button data-testid="billing-monthly" onClick={() => setYearly(false)}
              className={`px-5 py-2 rounded-full text-sm transition-colors ${!yearly ? "bg-cyan-400 text-[#030305] font-semibold" : "text-white/60"}`}>Monthly</button>
            <button data-testid="billing-yearly" onClick={() => setYearly(true)}
              className={`px-5 py-2 rounded-full text-sm transition-colors ${yearly ? "bg-cyan-400 text-[#030305] font-semibold" : "text-white/60"}`}>Yearly <span className="text-[10px] opacity-80">·save 20%</span></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14 items-start">
          {plans.map((plan) => {
            const Icon = ICONS[plan.tier] || Rocket;
            const current = user?.tier === plan.tier;
            return (
              <div key={plan.tier} data-testid={`plan-${plan.tier}`}
                className={`glass rounded-3xl p-8 relative ${plan.highlight ? "border-cyan-400/50 glow-cyan" : ""}`}>
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-tel text-[8px] px-3 py-1 rounded-full bg-cyan-400 text-[#030305]">Most Popular</span>
                )}
                <Icon size={30} weight="duotone" color={plan.highlight ? "#00D8FF" : "#A1A1AA"} />
                <h3 className="font-display font-bold text-2xl tracking-tight mt-5">{plan.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display font-black text-4xl tracking-tighter">{priceOf(plan)}</span>
                  {plan.tier !== "public" && <span className="text-white/40 text-sm mb-1">/{yearly ? "yr" : "mo"}</span>}
                </div>
                <ul className="mt-7 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-white/70">
                      <Check size={17} weight="bold" className="text-cyan-400 flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`choose-${plan.tier}`}
                  onClick={() => choose(plan)}
                  disabled={current || busy === plan.tier}
                  className={`w-full mt-8 py-3 rounded-full text-sm font-semibold transition-colors ${
                    current ? "bg-white/10 text-white/50 cursor-default"
                    : plan.highlight ? "bg-cyan-400 text-[#030305] glow-cyan hover:bg-cyan-300"
                    : "border border-white/20 text-white hover:border-cyan-400/60 hover:text-cyan-200"
                  }`}>
                  {current ? "Current Plan" : busy === plan.tier ? "Redirecting…" : plan.tier === "public" ? "Start Exploring" : "Upgrade"}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-tel text-[8px] text-white/30 mt-10">Secure checkout by Stripe · Test mode · Use card 4242 4242 4242 4242</p>
      </div>
    </div>
  );
}
